import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FileRejection } from 'react-dropzone';
import { PermissionsEnum } from 'consts/permissions';
import { NotificationMessages } from 'consts/notifications';
import { ACCEPTED_FILE_TYPE_IMAGE_VIDEO_ZIP } from 'consts/fileType';
import { FILE_SIZE_100MB } from 'modules/api/constants';
import { createCreative } from 'modules/api/content';
import Auth from 'modules/Auth';
import { cancelFunctions } from 'utils/cancelFunctions';
import FileUpload from 'lib/FileUpload';
import AsyncSelect from 'lib/AsyncSelect';
import Card from 'components/common/Card';
import { FilterDsp } from 'components/common/FilterArea/Filters';
import { CodeName } from 'components/common/types/CodeName.types';
import { addToastNotification } from 'store/notification/reducer';
import Footer from './Footer';
import { useFormState } from './useFormState';
import { useApiHandlers } from './useApiHandlers';
import InputWithValidation, { InputValidationConfig } from '../InputWithValidation';

const UploadCreativeContent: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { dsp, externalId, dealId, filesList, fileName, isSubmitDisabled, isFormValid } = useFormState();

  const { loadLookupData, createFormData } = useApiHandlers();

  const onExternalIdInputChange = useCallback(
    (_e, config: InputValidationConfig) => {
      externalId.set(config);
    },
    [externalId.set],
  );

  const onFileChange = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        dispatch(
          addToastNotification({ description: NotificationMessages.INVALID_UPLOAD, type: 'Warning', timeout: 10000 }),
        );
      } else {
        filesList.set(acceptedFiles);
        fileName.set(acceptedFiles[0].name);
      }
    },
    [dispatch, filesList.set, fileName.set],
  );

  const onUploadCreativeHandler = useCallback(async () => {
    if (!isFormValid) {
      dispatch(
        addToastNotification({ description: 'Please Enter Mandatory Details.', type: 'Warning', timeout: 2000 }),
      );
      return;
    }

    isSubmitDisabled.set(true);
    const formData = createFormData(dsp.get, externalId.get, dealId.get, filesList.get);

    try {
      await createCreative(cancelFunctions, formData);
      dispatch(addToastNotification({ description: 'Creative Added', type: 'Success', timeout: 2000 }));
      navigate('/content-management/creatives');
    } catch (error) {
      isSubmitDisabled.set(false);
    }
  }, [isFormValid, dsp.get, externalId.get, dealId.get, filesList.get, isSubmitDisabled.set]);

  const onCancelClick = useCallback(() => {
    navigate('/content-management/creatives');
  }, [navigate]);

  const onLookupSelect = useCallback(
    (dropdownName: string, newValue: CodeName | null) => {
      const selectedVal = newValue || ({} as CodeName);
      dsp.set(selectedVal);

      if (dropdownName === 'dsp') {
        dealId.set([]);
      }
    },
    [dsp.set, dealId.set],
  );

  const onLookupSelectMultiple = useCallback(
    (_dropdownName: string, newValue: CodeName[]) => {
      dealId.set([...newValue]);
    },
    [dealId.set],
  );

  const onLoadLookupData = useCallback(
    (dropdownName: string, query: string) => {
      return loadLookupData(dropdownName, query, dsp.get.code);
    },
    [loadLookupData, dsp.get.code],
  );

  return (
    <div className="row">
      <div className="col-12">
        <Card
          dataTestId="upload-creative"
          header="Upload creative"
          footer={
            <Footer
              onSubmitClick={onUploadCreativeHandler}
              onCancelClick={onCancelClick}
              isSubmitDisabled={isSubmitDisabled.get}
            />
          }
        >
          <div className="row pt-3">
            {Auth.hasPermission(PermissionsEnum.CONTENT_DSP_VIEW) && (
              <div className="col-12 col-md-6 col-lg-4">
                <div className="form-group mb-3">
                  <span className="sub-header-base form-label">
                    DSP name <i className="body-base text-pinkRed-500">*</i>
                  </span>
                  <FilterDsp
                    dataTestId="dsp"
                    selectedValue={dsp.get.code ? dsp.get : null}
                    onChange={onLookupSelect}
                    placeholder="Search DSP name"
                  />
                </div>
              </div>
            )}

            <div className="col-12 col-md-6 col-lg-4">
              <div className="form-group mb-3 border-b">
                <label htmlFor="file" className="text-sm font-medium text-gray-700">
                  Upload <i className="body-base text-pinkRed-500">*</i>
                </label>
                <FileUpload
                  acceptFileType={ACCEPTED_FILE_TYPE_IMAGE_VIDEO_ZIP}
                  maxFileSize={FILE_SIZE_100MB}
                  placeholder={fileName.get === '' ? 'Upload creative' : fileName.get}
                  onFileChange={onFileChange}
                />
              </div>
            </div>

            {Auth.hasPermission(PermissionsEnum.CONTENT_CREATIVE_ID_VIEW) && (
              <div className="col-12 col-md-6 col-lg-4">
                <div className="form-group mb-3">
                  <span className="sub-header-base form-label">
                    Creative ID <i className="body-base text-pinkRed-500">*</i>
                  </span>
                  <InputWithValidation inputConfig={externalId.get} onChange={onExternalIdInputChange} />
                </div>
              </div>
            )}
          </div>

          <div className="row pt-3">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="form-group mb-3">
                <span className="sub-header-base form-label">Campaign ID</span>
                <AsyncSelect
                  dataTestId="campaign-id"
                  name="dealId"
                  loadData={onLoadLookupData}
                  selectedValue={dealId.get.length ? dealId.get : null}
                  placeholder="Search campaign ID"
                  onSelect={onLookupSelectMultiple}
                  multiple
                  isDisabled={!dsp.get.code}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UploadCreativeContent;
// export default withLoader(withCancelRequest(withRouter(UploadCreativeContent)));
